
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/domains/auth/useAuth';
import { usePrivacySettings } from '@/hooks/settings';
import { AlertCircle, Download, Loader2, Shield, Trash2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface PrivacySettingsActionsProps {
  isLoading?: boolean;
  error?: boolean;
  onRetry?: () => void;
  noCard?: boolean;
}

const PrivacySettingsActions: React.FC<PrivacySettingsActionsProps> = ({ 
  error: propsError,
  noCard = false,
}) => {
  const { user } = useAuth();
  const [confirmText, setConfirmText] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  
  // Use our privacy settings hook
  const { 
    actions, 
    isExporting, 
    isDeleting, 
    isError, 
    error 
  } = usePrivacySettings(user!);

  // Custom confirmation dialog for account deletion
  const handleDeleteClick = () => {
    setDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (confirmText === 'DELETE') {
      actions.deleteAccount();
    }
    setDialogOpen(false);
    setConfirmText('');
  };

  // Show error states from mutations if needed
  const showExportError = isError || propsError;
  const showDeleteError = isError || propsError;

  const content = (
    <>
      <div className="space-y-1 mb-6">
        <h3 className="text-lg font-semibold flex items-center gap-2 text-destructive">
          <Trash2 className="h-5 w-5" />
          Delete Your Account
        </h3>
        <p className="text-sm text-muted-foreground">
          Permanently delete your account and all of your data
        </p>
      </div>
      <div>
        <p className="text-sm text-muted-foreground mb-4">
          Warning: This action is irreversible. Once you delete your account,
          all of your data will be permanently removed from our systems.
        </p>
        
        {showDeleteError && (
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {error instanceof Error 
                ? error.message 
                : "Failed to delete account. Please try again."}
            </AlertDescription>
          </Alert>
        )}
        
        <Alert className="mb-4 bg-destructive/10 border-destructive/20">
          <Shield className="h-4 w-4" />
          <AlertDescription className="font-medium">
            This action cannot be undone. Please be certain.
          </AlertDescription>
        </Alert>
      </div>
      <div className="flex justify-start">
        <AlertDialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <AlertDialogTrigger asChild>
            <Button 
              variant="destructive"
              onClick={handleDeleteClick}
              disabled={isDeleting}
            >
              {isDeleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete Account
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
              <AlertDialogDescription>
                This action cannot be undone. This will permanently delete your
                account and remove your data from our servers.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="py-4">
              <p className="text-sm font-medium mb-2">
                Type "DELETE" to confirm:
              </p>
              <input
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="DELETE"
              />
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel
                onClick={() => {
                  setConfirmText('');
                }}
              >
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleConfirmDelete}
                disabled={confirmText !== 'DELETE'}
                className="bg-destructive hover:bg-destructive/90"
              >
                Delete Account
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </>
  );

  if (noCard) {
    return content;
  }

  return (
    <div className="space-y-6">      
      {/* Delete Account Card */}
      <Card className="border-destructive/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-destructive">
            <Trash2 className="h-5 w-5" />
            Delete Your Account
          </CardTitle>
          <CardDescription>
            Permanently delete your account and all of your data
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4">
            Warning: This action is irreversible. Once you delete your account,
            all of your data will be permanently removed from our systems.
          </p>
          
          {showDeleteError && (
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {error instanceof Error 
                  ? error.message 
                  : "Failed to delete account. Please try again."}
              </AlertDescription>
            </Alert>
          )}
          
          <Alert className="mb-4 bg-destructive/10 border-destructive/20">
            <Shield className="h-4 w-4" />
            <AlertDescription className="font-medium">
              This action cannot be undone. Please be certain.
            </AlertDescription>
          </Alert>
        </CardContent>
        <CardFooter>
          <AlertDialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <AlertDialogTrigger asChild>
              <Button 
                variant="destructive"
                onClick={handleDeleteClick}
                disabled={isDeleting}
              >
                {isDeleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Delete Account
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                <AlertDialogDescription>
                  This action cannot be undone. This will permanently delete your
                  account and remove your data from our servers.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <div className="py-4">
                <p className="text-sm font-medium mb-2">
                  Type "DELETE" to confirm:
                </p>
                <input
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  type="text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder="DELETE"
                />
              </div>
              <AlertDialogFooter>
                <AlertDialogCancel
                  onClick={() => {
                    setConfirmText('');
                  }}
                >
                  Cancel
                </AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleConfirmDelete}
                  disabled={confirmText !== 'DELETE'}
                  className="bg-destructive hover:bg-destructive/90"
                >
                  Delete Account
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </CardFooter>
      </Card>
    </div>
  );
};

export default React.memo(PrivacySettingsActions);
